import React, { useState } from 'react';

/**
 * Dasha Tables — Future Dasha Tables with 5-level navigation tabs.
 *
 * Tabs: Mahadasha | Antardasha | Pratyantardasha | Sookshma | Prana
 *
 * Props:
 *   - vimshottari: data.vimshottari from API
 *   - onClickRow: (dashaObj, level) => void
 */

const TAB_DEFS = [
  { key: 'mahadasha',       label: 'महादशा',      short: 'MD',  showNext: 4 },
  { key: 'antardasha',      label: 'अंतर्दशा',     short: 'AD',  showNext: 6 },
  { key: 'pratyantardasha', label: 'प्रत्यंतर्दशा',  short: 'PD',  showNext: 6 },
  { key: 'sookshma',        label: 'सूक्ष्म',       short: 'SD',  showNext: 6 },
  { key: 'prana',           label: 'प्राण',        short: 'PrD', showNext: 6 },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function findCurrentIdx(list) {
  if (!list || list.length === 0) return -1;
  const now = new Date();
  for (let i = 0; i < list.length; i++) {
    const s = new Date(list[i].start);
    const e = new Date(list[i].end);
    if (s <= now && now < e) return i;
  }
  return -1;
}

export default function DashaTables({ vimshottari, onClickRow }) {
  const [tab, setTab] = useState('mahadasha');
  if (!vimshottari) return null;
  const def = TAB_DEFS.find(t => t.key === tab);
  const list = vimshottari[tab] || [];
  const curIdx = findCurrentIdx(list);

  // Show current + next N
  const slice = curIdx >= 0
    ? list.slice(curIdx, Math.min(curIdx + 1 + def.showNext, list.length))
    : list.slice(0, def.showNext + 1);

  return (
    <div data-testid="dasha-tables" className="space-y-3">
      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5">
        {TAB_DEFS.map(t => (
          <button
            key={t.key}
            data-testid={`dasha-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${tab === t.key
              ? 'bg-gold/25 border-gold/60 text-gold shadow-md shadow-gold/20'
              : 'bg-white/5 border-white/10 text-text-muted hover:bg-white/10 hover:text-gold'}`}
          >
            {t.short} · {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gold/20">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-gold/80 bg-purple-950/40">
            <tr>
              <th className="text-left py-2 px-3">#</th>
              <th className="text-left py-2 px-3">स्वामी</th>
              <th className="text-left py-2 px-3">प्रारंभ</th>
              <th className="text-left py-2 px-3">समाप्ति</th>
              <th className="text-right py-2 px-3">अवधि</th>
              <th className="text-center py-2 px-3">वर्तमान</th>
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-center text-text-muted">कोई डेटा नहीं</td></tr>
            )}
            {slice.map((d, i) => {
              const absoluteIdx = (curIdx >= 0 ? curIdx : 0) + i;
              const isCurrent = absoluteIdx === curIdx;
              return (
                <tr
                  key={`${tab}-${absoluteIdx}`}
                  data-testid={`dasha-row-${tab}-${absoluteIdx}`}
                  onClick={() => onClickRow && onClickRow(d, tab)}
                  className={`border-t border-white/5 cursor-pointer hover:bg-white/5 ${isCurrent ? 'bg-gold/10' : ''}`}
                >
                  <td className="py-2 px-3 text-text-muted">{absoluteIdx + 1}</td>
                  <td className={`py-2 px-3 font-semibold ${isCurrent ? 'text-gold' : 'text-text-main'}`}>{d.lord_hi}</td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtDate(d.start)}</td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtDate(d.end)}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{d.years.toFixed(2)}Y</td>
                  <td className="py-2 px-3 text-center">{isCurrent ? <span className="text-gold">●</span> : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
